/**
 * Mutable round state over typed arrays plus the primitive rule operations.
 * Everything is integer math; the same sequence of operations produces the
 * same state on client, server and solver.
 */
import { Hasher } from './hash.js';
import type { CompiledLevel } from './level.js';
import { rngSeed, type RngState } from './rng.js';
import {
  DEFAULT_ROUND_CONFIG,
  FailReason,
  Kind,
  Status,
  type RoundConfig,
  type RoundEvent,
  type RoundOutcome,
} from './types.js';

export const PIECE_BOARD = 0;
export const PIECE_BAG = 1;
export const PIECE_GONE = 2;
/** Physical bag limit: 7 + extra slot + rescues, with headroom; rescues stop growing the bag here. */
export const BAG_MAX = 16;
/**
 * Bag storage: one slot over BAG_MAX, because a glued pair inserts both tiles before the bag-full
 * check (a pair tapped into the last free slot briefly holds cap + 1 items, then pops or fails).
 */
const BAG_STORE = BAG_MAX + 1;
export const COMBO_WINDOW_MS = 3000;

export interface Round {
  L: CompiledLevel;
  cfg: RoundConfig;
  seed: number;
  assist: number;
  status: Status;
  failReason: FailReason;
  rescueEligible: boolean;
  piece: Uint8Array; // PIECE_*
  cover: Uint8Array; // number of present pieces covering i
  item: Int16Array;
  ice: Uint8Array;
  hp: Uint8Array;
  bag: Int16Array;
  bagLen: number;
  cap: number;
  /**
   * bagLock: slots still locked (L.bagLock until the first ticket line completes, then 0). Not
   * hashed on its own: it is a function of `remaining` (hashed), since serving is irreversible.
   */
  locked: number;
  /**
   * lockKey: the padlocks are still shut (true from the start on a level with locks, until the
   * first key triple pops). Not hashed on its own: it is a function of hashed state (a key triple
   * has popped iff at least 3 pieces labelled with the key item are gone: only pops and magnets
   * remove labelled pieces, shuffle relabels board pieces only, and assist never swaps the key).
   */
  lockShut: boolean;
  /**
   * hiddenLine: the hidden line is still face down (true from the start on a level with one, until
   * its reveal). Not hashed on its own: it is a function of `remaining` (hashed): face down iff the
   * revealer line is still open and the hidden line is untouched (serving is irreversible).
   */
  lineHidden: boolean;
  hist: Int16Array; // collect order; entries may be popped (skipped on undo)
  histLen: number;
  remaining: Int16Array; // per order line
  taps: number;
  boosters: number;
  extraSlotUsed: boolean;
  rescues: number;
  tips: number;
  orderPops: number;
  clutterPops: number;
  chain: number;
  bestCombo: number;
  lastPopT: number;
  lastT: number;
  deadline: number; // 0 = no timer
  rng: RngState;
  /** per collect: bagLen | cap << 8; feeds the share-card emoji grid */
  trace: number[];
  /** wrapped pieces stay revealed once they have been uncovered (cosmetic, not hashed) */
  revealed: Uint8Array;
  outcome: RoundOutcome | null;
}

export interface RoundOptions {
  seed: number;
  assist?: number; // GP-09: 0..2 buried order tiles lifted
  config?: Partial<RoundConfig>;
}

export function createRound(L: CompiledLevel, opts: RoundOptions): Round {
  const n = L.n;
  const r: Round = {
    L,
    cfg: { ...DEFAULT_ROUND_CONFIG, ...opts.config },
    seed: opts.seed >>> 0,
    assist: opts.assist ?? 0,
    status: Status.Playing,
    failReason: FailReason.None,
    rescueEligible: false,
    piece: new Uint8Array(n),
    cover: L.cover0.slice(),
    item: L.item0.slice(),
    ice: L.ice0.slice(),
    hp: L.hp0.slice(),
    bag: new Int16Array(BAG_STORE).fill(-1),
    bagLen: 0,
    cap: L.bag - L.bagLock,
    locked: L.bagLock,
    lockShut: L.lockedTiles.length > 0,
    lineHidden: L.hiddenLine >= 0,
    hist: new Int16Array(n + BAG_STORE),
    histLen: 0,
    remaining: L.lineCount0.slice(),
    taps: 0,
    boosters: 0,
    extraSlotUsed: false,
    rescues: 0,
    tips: 0,
    orderPops: 0,
    clutterPops: 0,
    chain: 0,
    bestCombo: 0,
    lastPopT: -COMBO_WINDOW_MS - 1,
    lastT: 0,
    deadline: L.rushMs,
    rng: rngSeed(opts.seed, 'round'),
    trace: [],
    revealed: new Uint8Array(n),
    outcome: null,
  };
  for (let i = 0; i < n; i++) r.revealed[i] = !L.wrapped[i] || r.cover[i] === 0 || L.kind[i] !== Kind.Tile ? 1 : 0;
  // a dispenser shows its top item only; the rest are revealed as they come up
  for (let d = 0; d < L.nDisp; d++) for (let i = L.dispStart[d]! + 1; i < L.dispStart[d + 1]!; i++) r.revealed[i] = 0;
  if (r.assist > 0) applyAssist(r, r.assist);
  return r;
}

export function cloneRound(r: Round): Round {
  return {
    ...r,
    piece: r.piece.slice(),
    cover: r.cover.slice(),
    item: r.item.slice(),
    ice: r.ice.slice(),
    hp: r.hp.slice(),
    bag: r.bag.slice(),
    hist: r.hist.slice(),
    remaining: r.remaining.slice(),
    rng: r.rng.slice(),
    trace: r.trace.slice(),
    revealed: r.revealed.slice(),
  };
}

/** Trade two item labels on every piece (triples keep their positions). */
export function relabelItems(r: Round, a: number, b: number): void {
  for (let i = 0; i < r.L.n; i++) {
    if (r.item[i] === a) r.item[i] = b;
    else if (r.item[i] === b) r.item[i] = a;
  }
}

/** GP-09: apply the level's first `count` precomputed assist swaps (hidden; the level is logged in the header). */
function applyAssist(r: Round, count: number): void {
  const swaps = r.L.assistSwaps;
  for (let k = 0; k < count && k < swaps.length; k++) relabelItems(r, swaps[k]![0], swaps[k]![1]);
}

/**
 * hiddenLine: can the player see line l's item right now? Every line but a hidden line still face
 * down. The greedy player, the magnet and the assist probes only use what this shows.
 */
export function lineShown(r: Round, l: number): boolean {
  return !(r.lineHidden && l === r.L.hiddenLine);
}

export function orderItemMask(r: Round): Uint8Array {
  const m = new Uint8Array(r.L.items.length);
  for (let l = 0; l < r.L.nLines; l++) if (r.remaining[l]! > 0) m[r.L.lineItem[l]!] = 1;
  return m;
}

/** Ticket sets not yet served (both goals). */
export function setsRemaining(r: Round): number {
  let s = 0;
  for (let l = 0; l < r.L.nLines; l++) s += r.remaining[l]! / 3;
  return s;
}

/**
 * clearGoal: items still on the board: tiles, conveyor, dispenser, tray and shelf items (crates
 * are obstacles, not items). A clear-goal round is won when this is 0.
 */
export function itemsOnBoard(r: Round): number {
  let c = 0;
  for (let i = 0; i < r.L.n; i++) if (r.piece[i] === PIECE_BOARD && r.L.kind[i] !== Kind.Crate) c++;
  return c;
}

/**
 * collectGoal: golden board pieces not yet popped. A golden in the bag still counts; undo simply
 * returns that same marked piece to the board. Derived from the hashed `piece` array.
 */
export function goldenLeft(r: Round): number {
  let c = 0;
  for (const i of r.L.goldenTiles) if (r.piece[i] !== PIECE_GONE) c++;
  return c;
}

/**
 * Sets the round's goal still needs: on an order level the ticket sets not yet served
 * (setsRemaining); on a clear-goal level the sets still on the board, ceil(itemsOnBoard / 3) (items
 * already in the bag are in hand); on a collect-goal level ticket sets plus every golden piece
 * not yet popped (each needs at least one pop). 0 exactly when the goal is met. Rescue eligibility
 * after a bag-full or stuck fail compares this with `rescueThreshold`, and the outcome reports it.
 */
export function goalSetsRemaining(r: Round): number {
  return r.L.clear ? Math.ceil(itemsOnBoard(r) / 3) : setsRemaining(r) + (r.L.collect ? goldenLeft(r) : 0);
}

/** Is the round's goal met: ticket, full clear, or ticket plus every golden piece popped? */
export function isGoalMet(r: Round): boolean {
  return r.L.clear ? r.bagLen === 0 && itemsOnBoard(r) === 0 : isOrderComplete(r) && (!r.L.collect || goldenLeft(r) === 0);
}

/**
 * Glue: the partner still on the board that a tap on tile i also collects, or -1 (i is not
 * glued, or its partner already left the board: then i plays as a single tile).
 */
export function gluePartner(r: Round, i: number): number {
  const p = r.L.partner[i]!;
  return p >= 0 && r.piece[p] === PIECE_BOARD ? p : -1;
}

/** lockKey: is board tile i padlocked right now (a locked tile while the locks are shut)? */
export function isLocked(r: Round, i: number): boolean {
  return r.lockShut && r.L.locked[i] === 1;
}

/**
 * A board tile is free when it is uncovered, not padlocked and, if glued to a tile on the board,
 * that tile is uncovered too (a locked tile is never glued).
 */
function tileFree(r: Round, i: number): boolean {
  if (r.cover[i] !== 0) return false;
  if (r.lockShut && r.L.locked[i]) return false;
  const p = r.L.partner[i]!;
  return p < 0 || r.piece[p] !== PIECE_BOARD || r.cover[p] === 0;
}

/**
 * Dispenser d's top item (the piece shown on the machine), or -1 when the machine is empty. The
 * top may still be covered by tiles over the machine: it is tappable when its cover is 0.
 */
export function dispenserTop(r: Round, d: number): number {
  const L = r.L;
  for (let i = L.dispStart[d]!; i < L.dispStart[d + 1]!; i++) if (r.piece[i] === PIECE_BOARD) return i;
  return -1;
}

/** Items still in dispenser d. */
export function dispenserLeft(r: Round, d: number): number {
  const L = r.L;
  let c = 0;
  for (let i = L.dispStart[d]!; i < L.dispStart[d + 1]!; i++) if (r.piece[i] === PIECE_BOARD) c++;
  return c;
}

/**
 * Shelf s's ends: its first and its last piece still on the board (PIECE_BOARD), the same piece
 * when one is left, [-1, -1] when the shelf is empty. Holes are skipped: an undo can return an
 * item whose neighbour popped (or was magnetted) while it was in the bag, and the ends then step
 * over that neighbour's empty place. The rule is the same in every case, so nothing but the
 * hashed `piece` array is needed.
 */
export function shelfEnds(r: Round, s: number): [number, number] {
  const L = r.L;
  let first = -1;
  let last = -1;
  for (let i = L.shelfStart[s]!; i < L.shelfStart[s + 1]!; i++)
    if (r.piece[i] === PIECE_BOARD) {
      if (first < 0) first = i;
      last = i;
    }
  return [first, last];
}

/** Items still on shelf s. */
export function shelfLeft(r: Round, s: number): number {
  const L = r.L;
  let c = 0;
  for (let i = L.shelfStart[s]!; i < L.shelfStart[s + 1]!; i++) if (r.piece[i] === PIECE_BOARD) c++;
  return c;
}

/**
 * Is shelf piece i (on the board) one of its shelf's ends: no piece of the shelf still on the
 * board before it, or none after it? Allocation-free (the solver path).
 */
function isShelfEnd(r: Round, i: number): boolean {
  const L = r.L;
  const s = L.shelfOf[i]!;
  let left = true;
  for (let j = L.shelfStart[s]!; j < i; j++)
    if (r.piece[j] === PIECE_BOARD) {
      left = false;
      break;
    }
  if (left) return true;
  for (let j = i + 1; j < L.shelfStart[s + 1]!; j++) if (r.piece[j] === PIECE_BOARD) return false;
  return true;
}

/** Is piece i tappable right now? */
export function isTappable(r: Round, i: number): boolean {
  if (r.piece[i] !== PIECE_BOARD) return false;
  const k = r.L.kind[i];
  if (k === Kind.Crate) return false;
  if (k === Kind.Tile) return tileFree(r, i);
  if (k === Kind.Dispenser) return r.cover[i] === 0; // only the top item of an uncovered machine
  // tray: only its first piece, while uncovered (the tap pops all three); its 2nd and 3rd never
  if (k === Kind.Tray) return i === r.L.trayStart[r.L.trayOf[i]!] && r.cover[i] === 0;
  // shelf: only the leftmost and the rightmost item still on its shelf
  if (k === Kind.Shelf) return isShelfEnd(r, i);
  // conveyor: within the first `slots` remaining queue entries
  const L = r.L;
  const base = L.nBoard + L.nCrates;
  let ahead = 0;
  for (let j = base; j < i; j++) if (r.piece[j] === PIECE_BOARD) ahead++;
  return ahead < L.convSlots;
}

/**
 * Fill `out` with tappable pieces, return count. A free glued pair is one move, listed once by
 * its lower tile index (tapping either tile collects both). An uncovered tray is one move, listed
 * once by its first piece. A shelf lists both its ends (its left end first), the single one once.
 */
export function listTappable(r: Round, out: Int32Array): number {
  const L = r.L;
  let c = 0;
  if (!L.nGlue) {
    for (let i = 0; i < L.nBoard; i++) if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0 && !(r.lockShut && L.locked[i])) out[c++] = i;
  } else
    for (let i = 0; i < L.nBoard; i++) {
      if (r.piece[i] !== PIECE_BOARD || !tileFree(r, i)) continue;
      const p = L.partner[i]!;
      if (p >= 0 && p < i && r.piece[p] === PIECE_BOARD) continue; // the pair is listed at p
      out[c++] = i;
    }
  if (L.nConv) {
    const base = L.nBoard + L.nCrates;
    let seen = 0;
    for (let i = base; i < base + L.nConv && seen < L.convSlots; i++)
      if (r.piece[i] === PIECE_BOARD) {
        out[c++] = i;
        seen++;
      }
  }
  // dispensers: the top item of each uncovered machine (the stack covers every item below its top)
  for (let i = L.dispStart[0]!; i < L.trayStart[0]!; i++) if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0) out[c++] = i;
  // trays: the first piece of each uncovered tray (its three pieces leave together)
  for (let k = 0; k < L.nTrays; k++) {
    const i = L.trayStart[k]!;
    if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0) out[c++] = i;
  }
  // shelves: the first and the last item still on each shelf
  for (let s = 0; s < L.nShelves; s++) {
    let first = -1;
    let last = -1;
    for (let i = L.shelfStart[s]!; i < L.shelfStart[s + 1]!; i++)
      if (r.piece[i] === PIECE_BOARD) {
        if (first < 0) first = i;
        last = i;
      }
    if (first >= 0) out[c++] = first;
    if (last > first) out[c++] = last;
  }
  return c;
}

function removeFromBoard(r: Round, i: number, uncovered: number[] | null): void {
  const L = r.L;
  for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) {
    const u = L.underList[p]!;
    r.cover[u]!--;
    if (r.cover[u] === 0) {
      r.revealed[u] = 1;
      if (uncovered && r.piece[u] === PIECE_BOARD) uncovered.push(u);
    }
  }
}

function returnToBoard(r: Round, i: number): void {
  const L = r.L;
  for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) r.cover[L.underList[p]!]!++;
}

function bagSnapshot(r: Round): number[] {
  return Array.from(r.bag.subarray(0, r.bagLen));
}

/** Insert into the bag, grouped after the last identical item. Returns slot index. */
function bagInsert(r: Round, tile: number): number {
  const it = r.item[tile]!;
  let pos = r.bagLen;
  for (let s = r.bagLen - 1; s >= 0; s--)
    if (r.item[r.bag[s]!] === it) {
      pos = s + 1;
      break;
    }
  for (let s = r.bagLen; s > pos; s--) r.bag[s] = r.bag[s - 1]!;
  r.bag[pos] = tile;
  r.bagLen++;
  return pos;
}

function bagRemoveAt(r: Round, pos: number): void {
  for (let s = pos; s < r.bagLen - 1; s++) r.bag[s] = r.bag[s + 1]!;
  r.bagLen--;
  r.bag[r.bagLen] = -1;
}

function bagCount(r: Round, it: number): number {
  let c = 0;
  for (let s = 0; s < r.bagLen; s++) if (r.item[r.bag[s]!] === it) c++;
  return c;
}

/** Serve a popped set: `forced` line (magnet) or the first ticket line needing the item. Returns line or -1. */
function serve(r: Round, it: number, forced = -1): number {
  if (forced >= 0) {
    r.remaining[forced] = r.remaining[forced]! - 3;
    return forced;
  }
  for (let l = 0; l < r.L.nLines; l++)
    if (r.L.lineItem[l] === it && r.remaining[l]! > 0) {
      r.remaining[l] = r.remaining[l]! - 3;
      return l;
    }
  return -1;
}

/** Every pop damages crates that are currently uncovered. */
function damageCrates(r: Round, ev: RoundEvent[] | null): void {
  const L = r.L;
  if (!L.nCrates) return;
  // crates uncovered by this pop's own breakage take damage from the NEXT pop
  const hit: number[] = [];
  for (let i = L.nBoard; i < L.nBoard + L.nCrates; i++) if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0) hit.push(i);
  for (const i of hit) {
    r.hp[i]!--;
    if (r.hp[i] === 0) {
      r.piece[i] = PIECE_GONE;
      const unc: number[] | null = ev ? [] : null;
      removeFromBoard(r, i, unc);
      if (ev) {
        ev.push({ type: 'crateHit', crate: i, hp: 0 });
        if (unc!.length) ev.push({ type: 'uncovered', tiles: unc! });
      }
    } else if (ev) ev.push({ type: 'crateHit', crate: i, hp: r.hp[i]! });
  }
}

function comboBps(step: number): number {
  return Math.min(10000 + 1000 * step, 20000);
}

function popCoins(r: Round, base: number): number {
  const step = Math.max(0, r.chain - 2);
  return Math.floor((base * comboBps(step)) / 10000);
}

function registerPop(r: Round, t: number): void {
  r.chain = t - r.lastPopT <= COMBO_WINDOW_MS ? r.chain + 1 : 1;
  r.lastPopT = t;
  const step = Math.max(0, r.chain - 2);
  if (step > r.bestCombo) r.bestCombo = step;
}

/** Resolve a set of 3 (from bag pop or magnet): serve order or count clutter. */
function resolveSet(r: Round, it: number, t: number, tiles: number[], ev: RoundEvent[] | null, magnetLine = -1): void {
  const viaMagnet = magnetLine >= 0;
  registerPop(r, t);
  const line = serve(r, it, magnetLine);
  let coins: number;
  if (line >= 0) {
    r.orderPops++;
    coins = popCoins(r, r.cfg.coinsOrderPop);
  } else {
    r.clutterPops++;
    coins = popCoins(r, r.cfg.coinsClutterPop);
  }
  r.tips += coins;
  if (ev) {
    if (!viaMagnet)
      ev.push({ type: 'popped', item: it, tiles, chain: r.chain, comboStep: Math.max(0, r.chain - 2), coins });
    if (line >= 0)
      ev.push({ type: 'served', customer: r.L.lineCustomer[line]!, line, item: it, remaining: r.remaining[line]! });
    else ev.push({ type: 'clutter', item: it, coins });
  }
  if (line >= 0 && r.locked > 0 && r.remaining[line] === 0) unlockBag(r, line, ev);
  if (r.lockShut && it === r.L.lockKey) openLocks(r, ev);
  // hiddenLine: the revealer completing, or the hidden line served while face down, turns it up
  if (r.lineHidden && line >= 0 && (line === r.L.hiddenLine || (line === r.L.revealLine && r.remaining[line] === 0))) revealHidden(r, ev);
  // collectGoal: the marker is on the piece, so shuffle labels do not move it; all pieces of this
  // triple are already PIECE_GONE, and the event follows every unlock/reveal caused by the pop
  if (ev && r.L.collect) {
    const golden = tiles.filter((i) => r.L.golden[i]).sort((a, b) => a - b);
    if (golden.length) ev.push({ type: 'golden', tiles: golden, left: goldenLeft(r) });
  }
  // clearGoal/collectGoal: serving is irreversible, so the pop that completes the ticket is unique
  if (ev && line >= 0 && (r.L.clear || r.L.collect) && r.remaining[line] === 0 && isOrderComplete(r))
    ev.push({ type: 'orderDone', left: r.L.clear ? itemsOnBoard(r) : goldenLeft(r) });
  damageCrates(r, ev);
}

/**
 * hiddenLine: the face-down line is revealed for the rest of the round (event `lineRevealed`). Runs
 * inside the pop (or magnet) that completes the revealer line or serves the hidden line itself,
 * after its bagUnlocked and unlocked. Serving is irreversible: nothing hides it again (undo only
 * returns items still in the bag).
 */
function revealHidden(r: Round, ev: RoundEvent[] | null): void {
  r.lineHidden = false;
  const l = r.L.hiddenLine;
  if (ev) ev.push({ type: 'lineRevealed', customer: r.L.lineCustomer[l]!, line: l, item: r.L.lineItem[l]! });
}

/**
 * lockKey: the first key triple to pop (tap or magnet) opens every lock for the rest of the round.
 * Runs inside that pop, like unlockBag and after it, so a glued partner collected by the same tap,
 * and the bag-full and stuck checks, already see the open tiles. Popping is irreversible: nothing
 * re-locks them (undo only returns items still in the bag).
 */
function openLocks(r: Round, ev: RoundEvent[] | null): void {
  r.lockShut = false;
  if (ev) ev.push({ type: 'unlocked', key: r.L.lockKey, tiles: r.L.lockedTiles.slice() });
}

/**
 * bagLock: the first completed ticket line opens the locked slots for the rest of the round.
 * Runs inside the pop (or magnet) that completes the line, before the tap's bag-full check, so a
 * glued partner inserted after it already has the extra room. Rescues keep cap + locked within
 * BAG_MAX; the min is a guard.
 */
function unlockBag(r: Round, line: number, ev: RoundEvent[] | null): void {
  const slots = r.locked;
  r.locked = 0;
  r.cap = Math.min(BAG_MAX, r.cap + slots);
  if (ev) ev.push({ type: 'bagUnlocked', slots, cap: r.cap, line });
}

export function isOrderComplete(r: Round): boolean {
  for (let l = 0; l < r.L.nLines; l++) if (r.remaining[l]! > 0) return false;
  return true;
}

export function starsFor(taps: number, par: number, helped: boolean): number {
  if (!helped && taps <= par) return 3;
  if (taps <= Math.floor((par * 11) / 10)) return 2;
  return 1;
}

export function buildOutcome(r: Round, result: 'win' | 'fail'): RoundOutcome {
  const helped = r.boosters !== 0 || r.rescues > 0;
  const win = result === 'win';
  // a clear-goal win leaves nothing on the board: no sweep
  const sweepCoins = win ? itemsOnBoard(r) * r.cfg.coinsSweep : 0;
  const baseCoins = win ? r.L.rewardCoins : 0;
  let itemsServed = 0;
  for (let l = 0; l < r.L.nLines; l++) itemsServed += r.L.lineCount0[l]! - r.remaining[l]!;
  return {
    result,
    taps: r.taps,
    stars: win ? starsFor(r.taps, r.L.par, helped) : 0,
    baseCoins,
    tips: r.tips,
    sweepCoins,
    coins: baseCoins + r.tips + sweepCoins,
    bestCombo: r.bestCombo,
    boosters: r.boosters,
    rescues: r.rescues,
    durationMs: r.lastT,
    orderPops: r.orderPops,
    clutterPops: r.clutterPops,
    setsRemaining: goalSetsRemaining(r),
    itemsServed,
  };
}

function hasAnyTappable(r: Round): boolean {
  const L = r.L;
  for (let i = 0; i < L.nBoard; i++) if (r.piece[i] === PIECE_BOARD && tileFree(r, i)) return true;
  for (let i = L.nBoard + L.nCrates; i < L.nBoard + L.nCrates + L.nConv; i++) if (r.piece[i] === PIECE_BOARD) return true;
  for (let i = L.dispStart[0]!; i < L.trayStart[0]!; i++) if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0) return true;
  // a shelf with any item on it has an end to tap
  for (let i = L.shelfStart[0]!; i < L.n; i++) if (r.piece[i] === PIECE_BOARD) return true;
  return trayEscape(r);
}

export function fail(r: Round, reason: FailReason, ev: RoundEvent[] | null): void {
  r.status = Status.Failed;
  r.failReason = reason;
  const canRescue = r.rescues < r.cfg.maxRescues;
  r.rescueEligible =
    reason === FailReason.Timeout ? canRescue : canRescue && goalSetsRemaining(r) <= r.cfg.rescueThreshold;
  r.outcome = buildOutcome(r, 'fail');
  if (ev)
    ev.push({
      type: 'failed',
      reason: reason === FailReason.Timeout ? 'timeout' : reason === FailReason.Stuck ? 'stuck' : 'bagFull',
      rescueEligible: r.rescueEligible,
    });
}

/**
 * After any state change, in this order: the goal (win), bag full (fail), stuck (fail). The goal
 * is the complete ticket on an order level; on a clear-goal level it is an empty board and bag
 * (isGoalMet); on a collect-goal level it is the complete ticket and no golden pieces left. A
 * complete ticket alone does not end either secondary-goal round.
 */
export function settle(r: Round, ev: RoundEvent[] | null): void {
  if (r.status !== Status.Playing) return;
  if (isGoalMet(r)) {
    r.status = Status.Won;
    r.outcome = buildOutcome(r, 'win');
    if (ev) ev.push({ type: 'won', outcome: r.outcome });
    return;
  }
  if (r.bagLen >= r.cap) return fail(r, FailReason.BagFull, ev);
  if (!hasAnyTappable(r)) fail(r, FailReason.Stuck, ev);
}

/** Pieces that take part in cover: board tiles, dispenser items and tray pieces (crates leave through damageCrates). */
function spatial(r: Round, i: number): boolean {
  const k = r.L.kind[i];
  return k === Kind.Tile || k === Kind.Dispenser || k === Kind.Tray;
}

/** A shelf item left its shelf (tap or magnet): tell the client the shelf's new ends (event `shelved`). */
function shelved(r: Round, i: number, ev: RoundEvent[] | null): void {
  if (!ev) return;
  const s = r.L.shelfOf[i]!;
  ev.push({ type: 'shelved', shelf: s, tile: i, left: shelfLeft(r, s), ends: shelfEnds(r, s) });
}

/** A dispenser item left its machine: show the next one and tell the client (event `dispensed`). */
function dispensed(r: Round, i: number, ev: RoundEvent[] | null): void {
  const d = r.L.dispOf[i]!;
  const next = dispenserTop(r, d);
  if (next >= 0) r.revealed[next] = 1;
  if (ev) ev.push({ type: 'dispensed', dispenser: d, tile: i, next, left: dispenserLeft(r, d) });
}

/** Move piece i from the board into the bag and pop its triple if it makes one (no bag-full check). */
function collect(r: Round, i: number, t: number, ev: RoundEvent[] | null): void {
  r.piece[i] = PIECE_BAG;
  const unc: number[] | null = ev ? [] : null;
  if (spatial(r, i)) removeFromBoard(r, i, unc);
  const slot = bagInsert(r, i);
  r.hist[r.histLen++] = i;
  if (ev) ev.push({ type: 'collected', tile: i, slot, bag: bagSnapshot(r) });
  if (r.L.kind[i] === Kind.Dispenser) dispensed(r, i, ev);
  else if (r.L.kind[i] === Kind.Shelf) shelved(r, i, ev);
  if (ev && unc!.length) ev.push({ type: 'uncovered', tiles: unc! });
  const it = r.item[i]!;
  if (bagCount(r, it) >= 3) {
    const tiles: number[] = [];
    for (let s = r.bagLen - 1; s >= 0; s--) {
      const b = r.bag[s]!;
      if (r.item[b] === it) {
        tiles.push(b);
        r.piece[b] = PIECE_GONE;
        bagRemoveAt(r, s);
      }
    }
    tiles.reverse();
    resolveSet(r, it, t, tiles, ev);
  }
}

/**
 * tray: pop tray k's three pieces straight off the board (no bag insert, no history entry). All
 * three go PIECE_GONE before the cover under them drops, so `uncovered` lists only other pieces
 * (the tray's own never cover each other). Then the pop resolves as a bag pop does (resolveSet:
 * combo, serving, coins, bagLock, lockKey, hiddenLine, crates). Events: `tray`, `uncovered`, then
 * resolveSet's. The tap's trace entry and settle are doTap's.
 */
function popTray(r: Round, k: number, t: number, ev: RoundEvent[] | null): void {
  const a = r.L.trayStart[k]!;
  const tiles = [a, a + 1, a + 2];
  for (const i of tiles) r.piece[i] = PIECE_GONE;
  const unc: number[] | null = ev ? [] : null;
  for (const i of tiles) removeFromBoard(r, i, unc);
  if (ev) {
    ev.push({ type: 'tray', tray: k, tiles: [a, a + 1, a + 2] });
    if (unc!.length) ev.push({ type: 'uncovered', tiles: unc! });
  }
  // shuffle never relabels a tray and assist relabels all three: the pieces share one item
  resolveSet(r, r.item[a]!, t, tiles, ev);
}

/**
 * Tap primitive. Caller guarantees the piece is tappable and the round is playing.
 * `ev` = null is the allocation-free solver path.
 *
 * Glue (rules v2): a tile whose partner is still on the board collects both for one tap. Events:
 * `glued {tile, partner}`, then the tapped tile's `collected` (+ `uncovered`) and its pop if it
 * makes a triple (`popped`, `served`|`clutter`, `crateHit`...), then the same for the partner,
 * then `won`/`failed`. The bag-full check runs once, after both: a pair tapped into the last free
 * slot briefly holds cap + 1 items (the partner's `collected` slot can equal cap) and fails as
 * bag full unless the partner pops.
 *
 * Tray (rules v2): a tap on a tray's first piece pops the whole tray without the bag (popTray):
 * `tray`, `uncovered`, `popped`, `served`|`clutter` and what the pop causes, then `won`/`failed`.
 * It counts as a tap and pushes a trace entry like any tap; the bag is unchanged, so it can never
 * fill it.
 *
 * Shelf (rules v2): a tap on a shelf end collects it like a conveyor item (no cover changes):
 * `collected`, `shelved` (the shelf's new ends), then its pop if it makes a triple, then
 * `won`/`failed`. Its neighbour on the row is the new end from then on.
 */
export function doTap(r: Round, i: number, t: number, ev: RoundEvent[] | null): void {
  r.taps++;
  r.lastT = t;
  if (r.L.nTrays && r.L.kind[i] === Kind.Tray) {
    popTray(r, r.L.trayOf[i]!, t, ev);
    r.trace.push(r.bagLen | (r.cap << 8));
    settle(r, ev);
    return;
  }
  if (r.ice[i]! > 0) {
    r.ice[i]!--;
    if (ev) ev.push({ type: 'iceCracked', tile: i, ice: r.ice[i]! });
    return;
  }
  const p = r.L.nGlue ? gluePartner(r, i) : -1;
  if (p >= 0 && ev) ev.push({ type: 'glued', tile: i, partner: p });
  collect(r, i, t, ev);
  if (p >= 0) collect(r, p, t, ev);
  r.trace.push(r.bagLen | (r.cap << 8));
  settle(r, ev);
}

/**
 * Undo the most recent collected item still in the bag. Returns tile or -1. If that tile's glue
 * partner is in the bag too, the two came in with one tap: the partner goes back as well (see
 * undoPartner). A partner that popped or was magnetted stays gone.
 */
export function doUndo(r: Round): number {
  while (r.histLen > 0) {
    const tile = r.hist[--r.histLen]!;
    if (r.piece[tile] !== PIECE_BAG) continue;
    returnFromBag(r, tile);
    return tile;
  }
  return -1;
}

function returnFromBag(r: Round, tile: number): void {
  for (let s = 0; s < r.bagLen; s++)
    if (r.bag[s] === tile) {
      bagRemoveAt(r, s);
      break;
    }
  r.piece[tile] = PIECE_BOARD;
  // a dispenser item goes back on top of its machine: it covers the rest of the stack again. A
  // shelf item (no cover) is back at its own place in the row, an end again (undo is last in, first out)
  if (spatial(r, tile)) returnToBoard(r, tile);
}

/**
 * Second half of undoing a glued pair: after doUndo returned `tile`, send its partner back too
 * when the partner is still in the bag (both in the bag always means one tap brought them: a
 * glued tile enters alone only once its partner has left the board for good). The partner is the
 * pair's first-collected tile, so it is the next live history entry; its entry is dropped so a
 * later undo does not find it. Returns the partner or -1.
 */
export function undoPartner(r: Round, tile: number): number {
  const p = tile >= 0 ? r.L.partner[tile]! : -1;
  if (p < 0 || r.piece[p] !== PIECE_BAG) return -1;
  for (let h = r.histLen - 1; h >= 0; h--)
    if (r.hist[h] === p) {
      r.hist.copyWithin(h, h + 1, r.histLen);
      r.histLen--;
      break;
    }
  returnFromBag(r, p);
  return p;
}

/**
 * Tray escape from the one-free-slot dead end: an uncovered tray pops without taking a slot, so
 * a round with one free slot and no pair in the bag still has a move that is not a loss.
 * Allocation-free; false without trays.
 */
export function trayEscape(r: Round): boolean {
  const L = r.L;
  for (let k = 0; k < L.nTrays; k++) {
    const i = L.trayStart[k]!;
    if (r.piece[i] === PIECE_BOARD && r.cover[i] === 0) return true;
  }
  return false;
}

/**
 * Glue escape from the one-free-slot dead end: a free glued pair of two identical items with one
 * more of that item in the bag pops on its second insert. Allocation-free; false without glue.
 */
export function glueEscape(r: Round): boolean {
  const L = r.L;
  if (!L.nGlue) return false;
  for (let i = 0; i < L.nBoard; i++) {
    const p = L.partner[i]!;
    if (p <= i || r.piece[i] !== PIECE_BOARD || r.piece[p] !== PIECE_BOARD || r.cover[i] !== 0 || r.cover[p] !== 0) continue;
    if (r.item[i] === r.item[p] && bagCount(r, r.item[i]!) >= 1) return true;
  }
  return false;
}

export function bagSnapshotOf(r: Round): number[] {
  return bagSnapshot(r);
}

/**
 * Can the magnet take piece i? Any piece on the board (covered or not, conveyor queue included),
 * except a dispenser item below its machine's top (the magnet reaches the shown item only), a
 * padlocked tile (the padlock holds it until the key pops), a tray piece (the magnet takes loose
 * items, and a tray stays whole until it is tapped) and a shelf item that is not one of its
 * shelf's ends (the magnet reaches the ends only, like a machine's top). The pick is made before
 * any piece moves, so both ends of one shelf may go in one magnet.
 */
function magnetable(r: Round, i: number): boolean {
  if (r.piece[i] !== PIECE_BOARD) return false;
  if (r.lockShut && r.L.locked[i]) return false;
  if (r.L.trayOf[i]! >= 0) return false;
  if (r.L.shelfOf[i]! >= 0) return isShelfEnd(r, i);
  const d = r.L.dispOf[i]!;
  return d < 0 || i === dispenserTop(r, d);
}

/**
 * The magnet serves the open ticket line with the most items left that has 3 magnetable pieces.
 * On clear-goal and collect-goal levels it works the same (its triple may advance the secondary
 * goal); once the ticket is complete there is no line to serve and the magnet is not available.
 * A hidden line still face down is not a candidate (the player cannot name its item; the magnet
 * would reveal it).
 */
export function magnetPick(r: Round): { line: number; tiles: number[] } | null {
  const L = r.L;
  let best = -1;
  for (let l = 0; l < L.nLines; l++) {
    if (r.remaining[l]! <= 0 || (r.lineHidden && l === L.hiddenLine)) continue;
    if (best < 0 || r.remaining[l]! > r.remaining[best]!) {
      const it = L.lineItem[l]!;
      let c = 0;
      for (let i = 0; i < L.n; i++) if (r.item[i] === it && magnetable(r, i)) c++;
      if (c >= 3) best = l;
    }
  }
  if (best < 0) return null;
  const it = L.lineItem[best]!;
  const cands: number[] = [];
  for (let i = 0; i < L.n; i++) if (r.item[i] === it && magnetable(r, i)) cands.push(i);
  cands.sort((a, b) => {
    const ca = r.cover[a] === 0 ? 0 : 1;
    const cb = r.cover[b] === 0 ? 0 : 1;
    return ca - cb || L.layer[b]! - L.layer[a]! || a - b;
  });
  return { line: best, tiles: cands.slice(0, 3) };
}

export function doMagnet(r: Round, t: number, ev: RoundEvent[] | null): boolean {
  const pick = magnetPick(r);
  if (!pick) return false;
  r.lastT = t;
  const unc: number[] | null = ev ? [] : null;
  for (const i of pick.tiles) {
    r.piece[i] = PIECE_GONE;
    if (spatial(r, i)) removeFromBoard(r, i, unc);
  }
  const it = r.L.lineItem[pick.line]!;
  if (ev) ev.push({ type: 'magnet', item: it, tiles: pick.tiles, line: pick.line, remaining: r.remaining[pick.line]! - 3 });
  // at most one item per machine (only its top is magnetable), so each `next` is the new top
  if (r.L.nDisp) for (const i of pick.tiles) if (r.L.kind[i] === Kind.Dispenser) dispensed(r, i, ev);
  // up to two items per shelf (its two ends): each `shelved` reports the shelf as the magnet leaves it
  if (r.L.nShelves) for (const i of pick.tiles) if (r.L.kind[i] === Kind.Shelf) shelved(r, i, ev);
  if (ev && unc!.length) ev.push({ type: 'uncovered', tiles: unc!.filter((u) => r.piece[u] === PIECE_BOARD) });
  resolveSet(r, it, t, pick.tiles, ev, pick.line);
  settle(r, ev);
  return true;
}

export function stateHash(r: Round): string {
  const h = new Hasher();
  h.int(r.status).int(r.failReason).int(r.cap).int(r.taps).int(r.boosters).int(r.rescues);
  h.int(r.tips).int(r.chain).int(r.bestCombo).int(r.lastT).int(r.deadline).int(r.lastPopT);
  h.int(r.extraSlotUsed ? 1 : 0).int(r.rescueEligible ? 1 : 0).ints(r.hist, r.histLen);
  h.ints(r.piece).ints(r.item).ints(r.ice).ints(r.hp).ints(r.bag, r.bagLen).ints(r.remaining).ints(r.rng);
  return h.hex();
}
